import { Add, DeleteOutline } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Grid2 as Grid,
  Switch,
  TextField,
  Typography,
  IconButton as MuiIconButton,
  Link,
} from "@mui/material";
import { FC } from "react";
import { useFormContext, Controller, useFieldArray } from "react-hook-form";
import { useGetPartnerDropdownsQuery } from "src/shared/api/dropdowns/partner";
import {
  SelectField,
  ContentAccordion,
  AutocompleteField,
} from "src/shared/components";
import {
  clientRegionOptionsBY,
  clientRegionOptionsRU,
} from "src/shared/constants/selectOptions/selectOptions";
import { EAgencies } from "src/shared/types/common.types";
import { EClientRegion } from "src/shared/types/partner.types";
import { regexps } from "src/shared/utils/hookFormRules";
import { messages } from "src/shared/utils/hookFormRules/messages";
import { IPartnerFormValues } from "../../model/types";
import { IEditPartnerFormValues } from "../../model/types/partner";

const getRegionOptions = (agency: EAgencies | "") => {
  switch (agency) {
    case EAgencies.IContext: {
      return clientRegionOptionsBY;
    }
    case EAgencies.Webcom_BY: {
      return clientRegionOptionsBY;
    }
    case EAgencies.Webcom_RU: {
      return clientRegionOptionsRU;
    }
    default:
      return [clientRegionOptionsRU[0], ...clientRegionOptionsBY];
  }
};

export const Activity: FC = () => {
  const { data: dropdowns } = useGetPartnerDropdownsQuery();
  const { activity_sphere, carrot_quest, easy_service, easy_service_phone } =
    dropdowns || {};
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<IPartnerFormValues | IEditPartnerFormValues>();
  const isEdinOn = watch("partner.is_EDMS_enabled");
  const agency = watch("partner.agency");
  const isWebcomRu = agency === EAgencies.Webcom_RU;
  const isByAgency =
    agency === EAgencies.Webcom_BY || agency === EAgencies.IContext;
  const regionOptions = getRegionOptions(agency);
  const {
    fields: edinEmailFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "partner.edin_email",
  });

  const hasErrors =
    errors.partner?.region ||
    errors.partner?.activity_spheres ||
    (errors.partner?.edin_email && isEdinOn);

  const legalEntityCertificateFile = watch(
    "partner.state_registration_certificate",
  );
  const currentLegalEntityCertificateFile =
    typeof legalEntityCertificateFile === "string"
      ? legalEntityCertificateFile
      : null;

  return (
    <ContentAccordion
      title={
        <Box display="flex" alignItems="center">
          <Chip label="6" />
          <Typography
            color={hasErrors ? "error" : "grey.500"}
            sx={{ ml: 2 }}
            component="span"
          >
            Деятельность
          </Typography>
        </Box>
      }
    >
      <Grid container spacing={4}>
        <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
          <Controller
            control={control}
            name="partner.region"
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <SelectField<EClientRegion | "">
                fullWidth
                inputRef={ref}
                {...field}
                label="Регион"
                error={!!error}
                helperText={error?.message}
                value={field.value}
                options={regionOptions}
              />
            )}
            rules={{ required: messages.required }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
          <Controller
            control={control}
            name="partner.activity_spheres"
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <AutocompleteField<number, true>
                fullWidth
                multiple
                {...field}
                inputRef={ref}
                label="Сфера деятельности"
                error={!!error}
                helperText={error?.message}
                value={field.value ? field.value : undefined}
                options={activity_sphere || []}
              />
            )}
            rules={{
              required: messages.required,
              validate: (v) => {
                if (v.length > 5) {
                  return "Максимум 5";
                }

                return true;
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
          <Controller
            control={control}
            name="partner.easy_service"
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <SelectField
                inputRef={ref}
                fullWidth
                {...field}
                label="Easy Service"
                error={!!error}
                helperText={error?.message}
                options={easy_service}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
          <Controller
            control={control}
            name="partner.carrot_quest"
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <SelectField<number | null>
                inputRef={ref}
                fullWidth
                {...field}
                label="Carrot Quest"
                error={!!error}
                helperText={error?.message}
                options={carrot_quest}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
          <Controller
            control={control}
            name="partner.easy_service_phone"
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <SelectField<number>
                inputRef={ref}
                fullWidth
                {...field}
                label="Телефония"
                error={!!error}
                helperText={error?.message}
                options={easy_service_phone}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4, lg: 2 }}>
          <Controller
            name="partner.is_in_blacklist"
            render={({ field }) => (
              <FormControlLabel
                control={<Switch {...field} checked={!!field.value} />}
                label="Black list"
              />
            )}
          />
        </Grid>
        {isByAgency || isWebcomRu ? (
          <Grid size={12}>
            <Controller
              name="partner.is_EDMS_enabled"
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch {...field} checked={!!field.value} />}
                  label={`Контрагент работает по ${isWebcomRu ? "Диадок" : "ЭДО"}`}
                />
              )}
            />
          </Grid>
        ) : null}
        {isEdinOn && isByAgency ? (
          <>
            {edinEmailFields.map((field, index) => (
              <Grid key={field.id} size={12}>
                <Box display="flex" alignItems="start" gap={4}>
                  <Controller
                    control={control}
                    name={`partner.edin_email.${index}.email`}
                    render={({
                      field: { ref, ...field },
                      fieldState: { error },
                    }) => (
                      <TextField
                        inputRef={ref}
                        sx={{ width: { xs: "100%", sm: "50%", lg: "33.3%" } }}
                        {...field}
                        label="Email для отправки в ЭДО"
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                    rules={{
                      required: messages.required,
                      pattern: {
                        value: regexps.emailRegexp,
                        message: messages.email,
                      },
                    }}
                  />
                  {index > 0 && (
                    <MuiIconButton
                      size="small"
                      color="inherit"
                      sx={{ minWidth: 0, color: "grey.600" }}
                      onClick={() => remove(index)}
                    >
                      <DeleteOutline color="inherit" />
                    </MuiIconButton>
                  )}
                </Box>
              </Grid>
            ))}
            <Grid size={12}>
              <Button
                variant="contained"
                startIcon={<Add />}
                disabled={edinEmailFields.length >= 5}
                onClick={() => append({ email: "" })}
              >
                Добавить Email
              </Button>
            </Grid>
          </>
        ) : null}
      </Grid>
      <Grid
        container
        spacing={2}
        sx={{ alignItems: "center", marginTop: "1rem" }}
      >
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="partner.state_registration_certificate"
            control={control}
            rules={{
              validate: (file) => {
                if (typeof file === "string") return true;

                if (file) {
                  const allowedTypes = [
                    "application/pdf",
                    "image/jpeg",
                    "image/svg+xml",
                  ];
                  if (!allowedTypes.includes(file.type)) {
                    return "Допустимые форматы: PDF, JPEG, JPG SVG";
                  }
                  if (file.size > 5_000_000) {
                    return "Максимальный размер: 5MB";
                  }
                }

                return true;
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                label="Свидетельство ЮЛ"
                type="file"
                inputRef={field.ref}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  field.onChange(
                    (e.target as HTMLInputElement).files?.[0] || null,
                  );
                }}
                error={!!error}
                helperText={error?.message}
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  accept: "application/pdf, image/jpeg, image/svg+xml",
                }}
              />
            )}
          />
        </Grid>

        {/* Блок для отображения текущего файла */}
        <Grid size={{ xs: 12, sm: 6 }}>
          {currentLegalEntityCertificateFile && (
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="body2">Текущий файл:</Typography>
              <Link
                href={currentLegalEntityCertificateFile}
                target="_blank"
                rel="noopener"
              >
                Просмотреть файл
              </Link>
            </Box>
          )}
        </Grid>
      </Grid>
    </ContentAccordion>
  );
};
